import { clampMOS, LIMITING_FACTOR_SCORE_CEILING } from 'utils/webrtc/stats/samba-scores';
import { ScoringResult, StatsGraph } from 'utils/webrtc/stats/types';

// Upstream ObserveRTC per-PC stability thresholds (client-monitor-js
// _calculatePeerConnectionStabilityScore).
const LOSS_TIER_LOW = 0.01;
const LOSS_TIER_MID = 0.05;
const LOSS_TIER_HIGH = 0.2;
const RTT_TIER_LOW_MS = 150;
const RTT_TIER_HIGH_MS = 300;
const MAX_PENALTY = 5;

// Below this many delta packets one lost packet swings the fraction wildly;
// skip the tick. Starting value; tune via the offline dump replay.
const MIN_PACKETS_PER_TICK = 20;

export type PCStability = { factor: number; worst: 'loss' | 'rtt' | null };

const lossPenaltyOf = (lossFraction: number): number => {
  if (lossFraction >= LOSS_TIER_HIGH) {
    return 5;
  }
  if (lossFraction >= LOSS_TIER_MID) {
    return 2;
  }
  if (lossFraction > LOSS_TIER_LOW) {
    return 1;
  }
  return 0;
};

const rttPenaltyOf = (rttMs: number | null): number => {
  if (rttMs === null) {
    return 0;
  }
  if (rttMs > RTT_TIER_HIGH_MS) {
    return 2;
  }
  if (rttMs > RTT_TIER_LOW_MS) {
    return 1;
  }
  return 0;
};

export function pcStabilityFactor(graph: StatsGraph): PCStability {
  let inboundLost = 0;
  let inboundReceived = 0;
  let outboundLost = 0;
  let outboundSent = 0;

  graph.inboundAudio.forEach((f) => {
    inboundLost += Math.max(0, f.packetsLost);
    inboundReceived += Math.max(0, f.packetsReceived);
  });
  graph.inboundVideo.forEach((f) => {
    inboundLost += Math.max(0, f.packetsLost);
    inboundReceived += Math.max(0, f.packetsReceived);
  });
  graph.outboundAudio.forEach((f) => {
    outboundLost += Math.max(0, f.packetsLost);
    outboundSent += Math.max(0, f.packetsSent);
  });
  graph.outboundVideo.forEach((f) => {
    outboundLost += Math.max(0, f.packetsLost);
    outboundSent += Math.max(0, f.packetsSent);
  });

  // Inbound loss is over lost+received; outbound (remote-inbound) loss is a
  // subset of sent — the blended denominator reduces to the spec's formula on
  // single-direction graphs (pub / ss-pub / rcv all are).
  const denominator = inboundLost + inboundReceived + outboundSent;
  if (denominator < MIN_PACKETS_PER_TICK) {
    return { factor: 1, worst: null };
  }

  const lossPenalty = lossPenaltyOf((inboundLost + outboundLost) / denominator);
  const rttPenalty = rttPenaltyOf(
    graph.transportRoundTripTime === null ? null : graph.transportRoundTripTime * 1000
  );

  const factor = Math.max(0, MAX_PENALTY - lossPenalty - rttPenalty) / MAX_PENALTY;
  if (factor === 1) {
    return { factor: 1, worst: null };
  }
  return { factor, worst: lossPenalty >= rttPenalty ? 'loss' : 'rtt' };
}

export function applyPcStability(result: ScoringResult, stability: PCStability): ScoringResult {
  if (result.score === null || stability.factor === 1) {
    return result;
  }
  const adjusted = clampMOS(result.score * stability.factor);
  const limitingFactor =
    stability.worst !== null && adjusted < LIMITING_FACTOR_SCORE_CEILING
      ? stability.worst
      : result.limitingFactor;
  return { ...result, score: adjusted, limitingFactor };
}
